import { createClass as _createClass, classCallCheck as _classCallCheck } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { FilmstripItemVisibility } from './ItemTracker.js';
import { FilmstripPageChangeType } from './PageChange.js';
import { getFirstFocusableEl, isTabbableEl, tabbableCandidateSelector } from './util/a11yUtils.js';

var hidden = FilmstripItemVisibility.hidden,
    partial = FilmstripItemVisibility.partial,
    visible = FilmstripItemVisibility.visible;
/**
 * @param el: the tile to act upon
 * @param shown: whether or not we're showing. If false, we're hiding
 */

var setTabIndex = function setTabIndex(shown, el) {
  if (isTabbableEl(el)) {
    if (shown) {
      return el.removeAttribute('tabindex');
    }

    return el.setAttribute('tabindex', '-1');
  }

  var tabbableChildren = el.querySelectorAll(tabbableCandidateSelector);
  /* eslint-disable unicorn/no-for-loop */

  for (var i = 0; i < tabbableChildren.length; i++) {
    if (shown) tabbableChildren[i].removeAttribute('tabindex');else tabbableChildren[i].setAttribute('tabindex', '-1');
  }
};

var Hiders = {
  ariaHidden: function ariaHidden(visibility, item) {
    if (visibility === hidden || visibility === partial) item.setAttribute('aria-hidden', 'true');else item.removeAttribute('aria-hidden');
  },
  tabIndex: function tabIndex(visibility, item) {
    var isFullyVisible = visibility === visible;
    setTabIndex(isFullyVisible, item);
  }
};
var A11yTracker = /*#__PURE__*/function () {
  function A11yTracker(childContainer, items, pages) {
    _classCallCheck(this, A11yTracker);

    this.childContainer = childContainer;
    this.items = items;
    this.pages = pages;
    this.hiders = [Hiders.ariaHidden, Hiders.tabIndex];
  }

  _createClass(A11yTracker, [{
    key: "destroy",
    value: function destroy() {}
    /** Decide based on an item's offset if it should be perceivable */

  }, {
    key: "applyHiders",
    value: function applyHiders(item, page, indexOnPage) {
      var _this = this;

      this.hiders.forEach(function (h) {
        return h(_this.itemShouldBePerceivable(page, indexOnPage, item), item);
      });
    }
    /** Set focus on an item */

  }, {
    key: "setFocus",
    value: function setFocus(el) {
      if (!(el instanceof HTMLElement)) return;
      var firstFocusableEl = getFirstFocusableEl(el, !!el.shadowRoot);

      if (!firstFocusableEl) {
        // add a warning if el either isn't a focusable element or does not have one as a child el
        // e.g. Accessibility warning: "DIV.testItem" is missing a required focusable element.

        /* eslint-disable no-console */
        console.warn("Accessibility warning: \"".concat(el.tagName, ".").concat(el.classList[0], "\" is missing a required focusable element. Adding a tabindex of -1 automatically.")); // the following is a fallback, but a Filmstrip's items should each contain a focusable el

        el.setAttribute('tabindex', '-1');
        el.focus();
      } else {
        firstFocusableEl.focus();
      }
    }
    /** Focus the first item on the current page. */

  }, {
    key: "focusFirstItem",
    value: function focusFirstItem() {
      var firstItem = this.pages.currentPage.firstItem;
      if (firstItem) this.setFocus(firstItem);
      this.childContainer.scroll(0, 0);
    }
    /** Focus the last item on the current page. */

  }, {
    key: "focusLastItem",
    value: function focusLastItem() {
      var lastItem = this.pages.currentPage.lastItem;
      if (lastItem) this.setFocus(lastItem);
      this.childContainer.scroll(0, 0);
    }
    /** The main entry point for a11y calculations and DOM setting. */

  }, {
    key: "setAttrs",
    value: function setAttrs(pageChangeType) {
      var _this2 = this;

      // if the PageTracker isn't ready, we should just bail
      if (this.items.renderedItems === 0 || !this.pages.isReady) return; // THE DECEPTIVELY COMPACT A11Y SETTER LOOP
      // Loop over every page; for every rendered item, decide if it should be perceivable

      this.pages.pageDescriptors.filter(function (page) {
        return page.isAtLeastPartiallyRendered;
      }).forEach(function (page) {
        page.items.forEach(function (item, indexOnPage) {
          return _this2.applyHiders(item, page, indexOnPage);
        });
      });
      if (pageChangeType === FilmstripPageChangeType.forward) this.focusFirstItem();
      if (pageChangeType === FilmstripPageChangeType.back) this.focusLastItem();
    }
    /** Returns a FilmstripItemVisibility, provided an item, the Page it's on, and its index on that
    page. */

  }, {
    key: "itemShouldBePerceivable",
    value: function itemShouldBePerceivable(page, indexOnPage, item) {
      var currentPage = this.pages.currentPage; // VISIBLE

      var itemIsOnCurrentPage = page === currentPage || page.hasOverlappingItems && currentPage.hasItem(item);
      if (itemIsOnCurrentPage) return visible; // PARTIALLY VISIBLE: check to see if this first item on this page is peeking out from behind
      // the right edge of the last one.

      var itemIsFirstOnPage = indexOnPage === 0;
      if (itemIsFirstOnPage && page.partiallyVisibleOnPreviousPage) return partial; // Otherwise HIDDEN

      return hidden;
    }
  }]);

  return A11yTracker;
}();

export { A11yTracker };
